import { useMutation } from '@apollo/client';
import { faLinkSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v4';
import {
  LithostratTargetType,
  type UploadStudyUpdateLithostratigraphyTabQuery,
} from '~/apollo/generated/v4/graphql';
import { NoItemsRow } from '~/components/common/NoItemsRow';
import { SortTrigger } from '~/components/common/SortTrigger';
import { Button } from '~/components/ui/button';
import { useSortFilter } from '~/hooks/data';
import {
  uploadLithostratFormationUpdateRoute,
  uploadLithostratGroupUpdateRoute,
  uploadLithostratMemberUpdateRoute,
} from '~/paths';
import { assertExhaustive } from '~/utils/common';
import type { RefetchQueries } from '~/utils/graphql';
import { readableGqlEnum } from '~/utils/text';

export const DELETE_LITHOSTRAT_STUDY_LINK = graphql(`
  mutation DeleteLithostratStudyLink($id: ID!) {
    lithostratStudyLinkDelete(id: $id) {
      result {
        id
      }
    }
  }
`);

type Study = NonNullable<
  UploadStudyUpdateLithostratigraphyTabQuery['studyGet']
>;
type LinkType = Study['lithostratStudyLinks'][number];

function linkPath(link: LinkType) {
  invariant(link.targetType && link.targetId);

  switch (link.targetType) {
    case LithostratTargetType.Group:
      return uploadLithostratGroupUpdateRoute(link.targetId);
    case LithostratTargetType.Formation:
      return uploadLithostratFormationUpdateRoute(link.targetId);
    case LithostratTargetType.Member:
      return uploadLithostratMemberUpdateRoute(link.targetId);
    default:
      throw assertExhaustive(link.targetType);
  }
}

type Props = {
  lithostratLinks: LinkType[];
  refetchQueries: RefetchQueries;
};

export function StudyLithostratLinksTable({
  lithostratLinks,
  refetchQueries,
}: Props) {
  const { items, sortIndicatorProps: siProps } = useSortFilter(
    lithostratLinks,
    'targetName',
    'targetName',
    'uploadStudyLithostratLinks',
  );

  const [deleteLink, { loading }] = useMutation(DELETE_LITHOSTRAT_STUDY_LINK, {
    refetchQueries,
  });

  const handleDelete = (id: number) => async () => {
    const confirmMessage = `The selected link will be will be removed.`;
    if (!confirm(confirmMessage)) return;

    try {
      await deleteLink({ variables: { id } });
      toast.success('Link deleted successfully.');
    } catch (err) {
      toast.error('There was a problem deleting the link.');
      console.log('Error deleting link:', err);
    }
  };

  return (
    <table className="table table-compact w-full">
      <thead>
        <tr>
          <th>
            <SortTrigger colName="targetName" sortIndicatorProps={siProps}>
              Name
            </SortTrigger>
          </th>
          <th>
            <SortTrigger
              colName="targetType"
              sortIndicatorProps={siProps}
              filterable
            >
              Lithostrat Type
            </SortTrigger>
          </th>
          <th />
        </tr>
      </thead>

      <tbody>
        <NoItemsRow show={!items.length} />

        {items.map(link => (
          <tr key={link.id}>
            <td>
              <Link to={linkPath(link)} className="link">
                {link.targetName}
              </Link>
            </td>
            <td>{readableGqlEnum(link.targetType)}</td>
            <td className="text-right">
              <Button
                type="button"
                color="ghost"
                size="xs"
                onClick={handleDelete(parseInt(link.id))}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faLinkSlash} />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
